/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class TransactionAPI
 * @hideconstructor
 * @classdesc API class that is used to control transactional operations across API calls
 */
define([
	'underscore',
	'../../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../TransactionAPI'
], (_, APIFactory, TransactionAPI) => {
	class Transaction {

		constructor() {
			this._transactions = new Map();
			this._handlers = new Map();
		}

		getAPI() {
			return APIFactory.createAPI(this, [TransactionAPI]);
		}

		startTransaction(parentToken) {
			if (!(parentToken && parentToken.transactionId)) {
				// create a new transaction entry
				const entry = this._createTransactionEntry(_.uniqueId('__transaction__'));
				if(parentToken && parentToken.source) {
					entry.token.source = parentToken.source;
					entry.token.eventGenerated = parentToken.eventGenerated;
				}
				return entry.token;
			} else {
				const entry = this._get(parentToken.transactionId);
				if (entry) {
					// add a sub-transaction to an existing transaction entry
					const subTransactionIdx = this._addSubTransaction(entry);
					let res = _.extend({ subTransaction: subTransactionIdx }, entry.token);
					if(parentToken && parentToken.source) {
						res.source = parentToken.source;
						res.eventGenerated = parentToken.eventGenerated;
					}
					return res;
				}
				console.warn('Invalid transactionId: ' + parentToken.transactionId);
				return parentToken;
			}
		}

		startTransactionById(transactionId) {
			let entry = this._get(transactionId);
			if (entry) {
				return this.startTransaction(entry.token);
			}
			entry = this._createTransactionEntry(transactionId);
			return entry.token;
		}

		endTransaction(token) {
			const transactionId = token && token.transactionId;
			let entry = this._get(transactionId);
			if (entry && Number.isInteger(token.subTransaction)) {
				entry = entry.children[token.subTransaction];
			}
			if (entry) {
				entry.timestamp.end = Date.now();
				entry.end();
			} else {
				console.warn('Invalid transactionId: ' + transactionId);
			}
			return token;
		}

		getTransactionById(id) {
			const entry = this._get(id);
			return entry ? entry.token : {};
		}

		registerTransactionHandler(token, handlerId, handler, args) {
			const isValid = this.isValidTransaction(token);

			if (isValid) {
				// register and invoke the handler
				this._register(handlerId, handler, token.transactionId, args);
				return this._invoke(handlerId);
			} else {
				// directly invoke the handler and move on
				const rv = handler([args]);
				return (rv instanceof Promise) ? rv : Promise.resolve();
			}
		}

		whenTransactionComplete(token) {
			// we only need wait for the root promise to complete
			const entry = this._get(token.transactionId);
			// return the transaction promise which disposes the transaction at the end
			return entry._promise.then(this._dispose.bind(this, token.transactionId));
		}

		isValidTransaction(token) {
			return !!this._get(token.transactionId);
		}

		getActiveTransactionList() {
			return _.map(this._getAll(), (entry) => {
				return entry.token;
			});
		}

		/**
		 * @private
		 * Create a new transaction entry
		 * @param {String} uniqueId unique transaction identifier
		 * {
		 *      token: {
		 *          transactionId: 'xyz'
		 *      },
		 *      end: function() { ... },
		 *      timestamp: {
		 *          start: 1551142727899,
		 *          end: 1551142741757
		 *      }
		 * }
		 */
		_createTransactionEntry(uniqueId) {
			const entry = {
				'token': {
					'transactionId' : uniqueId
				},
				'timestamp': {
					'start': Date.now()
				}
			};

			entry._promise = new Promise(function(resolve) {
				this.end = function() {
					if (this.children && this.children.length > 0) {
						this.children[0]._promise.then(resolve);
					} else {
						resolve();
					}
				}.bind(this);
			}.bind(entry));

			this._set(uniqueId, entry);

			return entry;
		}

		/**
		 * @private
		 * Add a sub-transcation to an existing transaction
		 * @param {Object} entry parent transaction entry object
		 * {
		 *      token: {
		 *          transactionId: 'xyz'
		 *      },
		 *      children: [{
		 *          end: function() { ... },
		 *          next: { ... }
		 *          timestamp: {
		 *              start: 1551142727899,
		 *              end: 1551142741757
		 *          }
		 *      }, {
		 *          end: function() { ... },
		 *          timestamp: {
		 *              start: 1551142727899,
		 *              end: 1551142741757
		 *          }
		 *      }]
		 *      end: function() { ... },
		 *      timestamp: {
		 *          start: 1551142727899
		 *      }
		 * }
		 */
		_addSubTransaction(entry) {
			// prepare the subtransactions list
			if (!entry.children) {
				entry.children = [];
			}

			const subTransactionEntry = {
				timestamp: {
					start: Date.now()
				}
			};
			const transactionIndex = entry.children.length;
			const lastItem = entry.children && entry.children[entry.children.length - 1];

			subTransactionEntry._promise = new Promise(function(resolve) {
				this.end = function() {
					if (this.next) {
						// resolve the my promise once the next promise is resolved
						this.next._promise.then(resolve);
					} else {
						resolve();
					}
				}.bind(this);
			}.bind(subTransactionEntry));

			// link the current transaction as the next transaction for the previous transaction
			// ensure the previous transaction waits for the next transaction since the previous transaction can already be resolved.
			if (lastItem) {
				lastItem.next = subTransactionEntry;
				lastItem._promise = Promise.all([lastItem._promise, subTransactionEntry._promise]);
			}

			// add the subtransaction to the existing entry
			entry.children.push(subTransactionEntry);

			return transactionIndex;
		}

		_getAll() {
			const all = [];
			this._transactions.forEach((tranaction) => { all.push(tranaction); });
			return all;
		}

		_get(transactionId) {
			return this._transactions.get(transactionId);
		}

		_set(transactionId, entry) {
			this._transactions.set(transactionId, entry);
		}

		_dispose(transactionId) {
			this._transactions.delete(transactionId);
		}

		_register(handlerId, handler, transactionId, args) {
			const entry = this._handlers.get(handlerId);
			if (entry) {
				// update the transactions related to current handler
				if (entry.transactionIds.indexOf(transactionId) < 0) {
					entry.transactionIds.push(transactionId);
				}
				entry.args.push(args);
			} else {
				// register the handler
				this._handlers.set(handlerId, {
					'transactionIds': [transactionId],
					'handler': handler,
					'args': [args]
				});
			}
		}

		_isRegistered(handlerId) {
			return !!this._handlers.get(handlerId);
		}

		/**
		 * This is an enhancement to replace Promise.all to handle cases
		 * which the array of transactions can grow asynchronously.
		 * Once the initial handler waits for all transactions complete,
		 * transactions that are added afterwards may result the handler
		 * to be invoked prematurely.
		 */
		_waitForAllTransactions(transactionIds, index = 0) {
			if (transactionIds.length > index) {
				// We can not rely on Promise.all since a new transaction can be added
				// after the handler was initially register and invoked
				const transaction = this._get(transactionIds[index]);
				if (transaction) {
					// wait for the current transaction to complete
					return transaction._promise.then(() => {
						// seek for the next transaction during the runtime
						return this._waitForAllTransactions(transactionIds, index + 1);
					});
				} else {
					// continue with the next transaction
					return this._waitForAllTransactions(transactionIds, index + 1);
				}
			}
			return Promise.resolve();
		}

		_invoke(handlerId) {
			const entry = this._handlers.get(handlerId);
			if (!entry._promise) {
				// @todo Use Transaction._waitForAllTransactions to support optimized rendering
				const transactions = _.map(entry.transactionIds, (id) => this._get(id)._promise);
				// invoke the handler once all related transactions are complete
				entry._promise = Promise.all(transactions).then(() => {
				// entry._promise = this._waitForAllTransactions(entry.transactionIds).then(() => {
					// dispose all transactions that are registered for this handler
					_.each(entry.transactionIds, this._dispose.bind(this));

					try {
						return entry.handler(entry.args);
					} finally {
						// clean up the handler
						this._handlers.delete(handlerId);
					}
				});
			}
			return entry._promise;
		}
	}

	return Transaction;
});
